import { useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useAuth } from "../../../context/AuthContext";
import { ItalianRenewalRow } from "../../../types/renewals";

const fmtMoney = (n: number | null) => {
  if (null === n || undefined === n) return "—";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(n);
};

const fmtDate = (d: string | null) => {
  if (!d) return "—";
  const part = d.substring(0, 10);
  const [y, m, day] = part.split("-");
  if (!y || !m || !day) return d;
  return `${day}/${m}/${y}`;
};

const fmtDateTime = (d: string | null) => {
  if (!d) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(d));
  } catch {
    return d;
  }
};

const fmt = (v: unknown) =>
  null === v || undefined === v || "" === v ? "—" : String(v);

const PENDIENTE = "Pendiente de integración";

type Props = {
  row: ItalianRenewalRow;
  onContactoSaved: (value: string | null) => void;
};

export default function RenewalExpandedRow({ row, onContactoSaved }: Props) {
  const { token } = useAuth();
  const [contacto, setContacto] = useState(row.contactoMariana ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = (row.contactoMariana ?? "") !== contacto;

  const saveContacto = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await request<{ contactoMariana: string | null }>(
        `/api/admin/operations/renewals-it/${row.leadUlid}/${row.renewalYear}/ops-note`,
        {
          method: "PUT",
          body: { contactoMariana: contacto.trim() || null },
          token,
        },
      );
      onContactoSaved(res.contactoMariana);
    } catch (err) {
      setError(
        err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo guardar",
      );
    } finally {
      setSaving(false);
    }
  };

  const blocks = [
    {
      title: "Comunicación",
      fields: [
        { label: "Expt. rnwl email date", value: <Pending /> },
        { label: "Renewal email sent", value: fmtDateTime(row.renewalEmailSentAt) },
        { label: "Rnwl acceptance", value: fmtDateTime(row.renewalAcceptanceAt) },
        { label: "Rnwl confirmation sent", value: fmtDateTime(row.renewalConfirmationSentAt) },
        { label: "Days until acceptance", value: fmt(row.daysUntilAcceptance) },
        { label: "Whatsapp template sent", value: <Pending /> },
        { label: "Longest call (datetime)", value: <Pending /> },
        { label: "Longest call duration (min)", value: <Pending /> },
        { label: "Rnwl reminder 1", value: <Pending /> },
        { label: "Rnwl reminder 2", value: <Pending /> },
        { label: "Rnwl reminder 3", value: <Pending /> },
      ],
    },
    {
      title: "Pricing",
      fields: [
        { label: "Rnwl monthly P", value: fmtMoney(row.renewalMonthlyPrice) },
        { label: "Rnwl yearly P", value: fmtMoney(row.renewalYearlyPrice) },
      ],
    },
    {
      title: "Suscripción",
      fields: [
        { label: "Susc status", value: fmt(row.subscriptionStatus) },
        {
          label: "First invoice paid",
          value: row.firstInvoicePaid === null ? "—" : row.firstInvoicePaid ? "Sí" : "No",
        },
        { label: "Renewal succeeded", value: row.renewalSucceeded ? "Sí" : "No" },
        { label: "Limit renewal date", value: fmtDate(row.limitRenewalDate) },
      ],
    },
  ];

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        padding: "1rem 1.5rem 1.25rem",
        background: "#f8f9fb",
        borderTop: "1px solid #e9ecef",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: "0.85rem",
        }}
      >
        {blocks.map((block) => (
          <section
            key={block.title}
            style={{
              background: "white",
              padding: "0.75rem 0.9rem",
              borderRadius: "0.55rem",
              borderLeft: "3px solid #0069d9",
              boxShadow: "0 1px 3px rgba(0,0,0,.04)",
            }}
          >
            <h4
              style={{
                margin: "0 0 0.55rem",
                fontSize: ".7rem",
                textTransform: "uppercase",
                fontWeight: 600,
                color: "#0069d9",
                letterSpacing: "0.5px",
              }}
            >
              {block.title}
            </h4>
            <dl style={{ margin: 0, display: "grid", gap: "0.3rem" }}>
              {block.fields.map((f) => (
                <div
                  key={f.label}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr",
                    fontSize: ".8rem",
                    gap: "0.5rem",
                  }}
                >
                  <dt style={{ color: "#73879C" }}>{f.label}</dt>
                  <dd style={{ margin: 0, color: "#1f2933" }}>{f.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}

        <section
          style={{
            background: "white",
            padding: "0.75rem 0.9rem",
            borderRadius: "0.55rem",
            borderLeft: "3px solid #856404",
            boxShadow: "0 1px 3px rgba(0,0,0,.04)",
            gridColumn: "1 / -1",
          }}
        >
          <h4
            style={{
              margin: "0 0 0.55rem",
              fontSize: ".7rem",
              textTransform: "uppercase",
              fontWeight: 600,
              color: "#856404",
              letterSpacing: "0.5px",
            }}
          >
            Contacto Mariana
          </h4>
          <textarea
            value={contacto}
            onChange={(e) => setContacto(e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder="Notas operativas sobre el contacto con el cliente…"
            style={{
              width: "100%",
              padding: "0.45rem 0.55rem",
              border: "1px solid #dee2e6",
              borderRadius: "0.4rem",
              fontSize: ".82rem",
              fontFamily: "inherit",
              background: "#f8f9fb",
              resize: "vertical",
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "0.5rem",
              gap: "0.75rem",
            }}
          >
            <span style={{ fontSize: ".75rem", color: "#73879C" }}>
              {error && <span style={{ color: "#b32424" }}>{error}</span>}
              {!error && dirty && "Cambios sin guardar"}
              {!error && !dirty && row.contactoMariana && "Sincronizado"}
            </span>
            <button
              type="button"
              onClick={saveContacto}
              disabled={!dirty || saving}
            >
              {saving ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

function Pending() {
  return (
    <span
      title={PENDIENTE}
      style={{ color: "#adb5bd", fontStyle: "italic", fontSize: ".78rem" }}
    >
      —
    </span>
  );
}
